import {calcUserScore, filterObjectByParams} from 'helpers/common-helper';
import Player from './player';
import Const from 'constants';
import Action from 'actions';
import {calcSympathyPoints, getDefaultExposure} from 'helpers/player-helper';
import {getPlayerModeProjOwnFunctionality} from 'helpers/projected-ownership';
import NumberHelper, {toFloat} from 'helpers/number-helper';

export default class PlayerShowdown extends Player {
    modes = [Const.CAPTAIN_MODE, Const.FLEX_MODE];

    preparePoolsOfPlayers({incomingValues: {players, filterPlayersBeforePreparingPools}}) {
        if (!filterPlayersBeforePreparingPools) {
            return this.exec('createPoolsOfPlayers', {
                players: Object.values(players),
            });
        }

        const {
            playersData: {filters},
        } = Action.getState();

        const params = {
            team: {
                filterType: Const.FILTER_TYPE_IN_ARRAY,
                filterValue: filters.games,
            },
        };

        players = filterObjectByParams(players, params, true);

        return this.exec('createPoolsOfPlayers', {
            players,
        });
    }

    resetPoints({incomingValues: {dispatch}}) {
        return this.exec('resetPoints_ShowDown', {dispatch});
    }

    resetSettings({incomingValues: {dispatch}}) {
        return this.exec('resetSettings_ShowDown', {dispatch});
    }

    createPoolsOfPlayers({incomingValues: {players}}) {
        return this.exec('createPoolsOfPlayers_ShowDown', {players});
    }

    getExpandedEntityParams({incomingValues = {}}) {
        const {modesConfig} = Action.getState();
        const {userPointsType = 'adminPoints'} = incomingValues;
        const expandedPlayer = super.getExpandedEntityParams({incomingValues});
        const userPoints = parseFloat(expandedPlayer[userPointsType]);
        const cptPointCoef = modesConfig.getConfig('CPT').getPointCoefficient();
        const grades = parseFloat(expandedPlayer.grades);
        const captainUserPoints = userPoints * cptPointCoef;
        const captainAdminPoints = expandedPlayer.adminPoints * cptPointCoef;
        const captainPoints = expandedPlayer.points * cptPointCoef;
        const captainSalary = expandedPlayer.salary * cptPointCoef;
        const captainFantasyPoints = expandedPlayer.fantasyPoints * cptPointCoef;
        const captainGrades = grades * cptPointCoef;
        const PlayerService = this;

        return {
            ...expandedPlayer,
            grades,
            [Const.EXCLUDED_BY_ORDER_RANGE]: false,
            [Const.EXCLUDED_BY_CONFIRMED_RANGE]: false,
            overall_exposureMax: getDefaultExposure('max'),
            expected_minutes: parseInt(expandedPlayer.expected_minutes),
            points_per_minute: parseFloat(expandedPlayer.points_per_minute),
            userPoints: userPoints,
            userScore: calcUserScore(userPoints, expandedPlayer.salary, this.userScoreMultiplier),
            lineupRole: Const.DEFAULT_MODE,
            captain_salary: captainSalary,
            captain_fantasyPoints: toFloat(captainFantasyPoints, 3),
            captain_exposureMax: expandedPlayer.exposureMax,
            captain_exposureMin: expandedPlayer.exposureMin,
            captain_points: toFloat(captainPoints, 3),
            captain_adminPoints: toFloat(captainAdminPoints, 3),
            captain_userPoints: toFloat(captainUserPoints, 3),
            captain_grades: toFloat(captainGrades, 3),
            captain_userScore: calcUserScore(captainUserPoints, captainSalary, this.userScoreMultiplier),
            captain_pointsChangedManually: false,
            projOwn: {...getPlayerModeProjOwnFunctionality(expandedPlayer, ['cpt', 'flex'])},
            isConfirmed() {
                return expandedPlayer.confirmed === Const.MLB_PLAYER_CONFIRMED;
            },
            isExcludedByFilter() {
                return this[Const.EXCLUDED_BY_CONFIRMED_RANGE] || this[Const.EXCLUDED_BY_ORDER_RANGE];
            },
            include() {
                this.includeBy([Const.EXCLUDED_BY_ORDER_RANGE, Const.EXCLUDED_BY_CONFIRMED_RANGE]);
            },
            resetExposure() {
                this.overall_exposureMax = getDefaultExposure('max');
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
                this.captain_exposureMin = getDefaultExposure('min');
                this.captain_exposureMax = getDefaultExposure('max');
            },
            getPropNameOfMode(prop, incomingMode) {
                let mode = incomingMode || this.mode;
                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getPropNameOfLineupRole(prop) {
                let propName = prop;

                if (this.getIsCaptain()) {
                    propName = this.getCapPropName(prop);
                }

                return propName;
            },
            getPropValueOfLineupRole(prop) {
                if (prop === 'projected_ownership') {
                    return this.projOwn.getValue(this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE);
                }

                let value,
                    propName = this.getPropNameOfLineupRole(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getCapPropName(prop) {
                let mode = this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getCapPropValue(prop) {
                if (prop === 'projected_ownership') {
                    return this.projOwn.getValue(Const.CAPTAIN_MODE);
                }

                let value,
                    propName = this.getCapPropName(prop);
                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }
                return value;
            },
            getIsCaptain() {
                return this.lineupRole === Const.CAPTAIN_MODE;
            },
            setAsCaptain() {
                this.lineupRole = Const.CAPTAIN_MODE;
            },
            isPitcher() {
                return this.positionName === 'P';
            },
            getLineupRole() {
                return this.lineupRole;
            },
            isFitForLineup(substitutablePlayer) {
                return true;
            },
        };
    }

    definePropNameOfMode({incomingValues: {prop, mode}}) {
        const captainPropsList = [
            'exposureMax',
            'exposureMin',
            'points',
            'adminPoints',
            'userPoints',
            'userScore',
            'salary',
            'grades',
            'fantasyPoints',
            'pointsChangedManually',
            'projected_ownership',
        ];
        const overallPropsList = ['exposureMax', 'projected_ownership'];

        if (Const.CAPTAIN_MODE === mode && inArray(prop, captainPropsList)) {
            return 'captain_' + prop;
        } else if (Const.OVERALL_MODE === mode && inArray(prop, overallPropsList)) {
            return 'overall_' + prop;
        }

        return prop;
    }

    loadExtraPlayerDataFromStore({incomingValues: {dispatch, data}}) {
        if (data.hasOwnProperty('rangeOfOutcomesByPositions')) {
            dispatch(Action.setRangeOfOutcomesByPositionsFromStorage(data.rangeOfOutcomesByPositions));
        }

        if (data.hasOwnProperty('playerConfirmed')) {
            dispatch(Action.set('PlayerConfirmedFilterFromStorage', data.playerConfirmed));
        }

        if (data.hasOwnProperty('hittersOrder')) {
            dispatch(Action.set('HittersOrderFromStorage', data.hittersOrder));
        }

        return this.exec('loadExtraPlayerDataFromStore_ShowDown', {dispatch, data});
    }

    getExtraPlayerDataToStore({...rest}) {
        return this.exec('getExtraPlayerDataToStore_ShowDown', {});
    }

    preparePlayerDataForDownloading({incomingValues: {player}}) {
        return this.exec('preparePlayerDataForDownloading_ShowDown', {player});
    }

    updatePlayersAfterRefreshingProjections({incomingValues: {indexedPlayers}}) {
        const {
            playersData: {players, games},
            playersSympathy,
        } = Action.getState();

        const result = {};

        Object.keys(indexedPlayers).map((key) => {
            const newPlayer = indexedPlayers[key];
            const player = {...players.getPlayer(newPlayer.getId())};

            if (isEmpty(player)) {
                result[key] = {...newPlayer};
                return;
            }

            const isGameActive = !games.isPlayerGameInactive(player);
            let userPoints = player.userPoints;
            let userScore = player.userScore;
            let captain_userPoints = player.captain_userPoints;
            let captain_userScore = player.captain_userScore;

            if (!player.pointsChangedManually && isGameActive) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId());
                userPoints = NumberHelper.float(
                    calcSympathyPoints(newPlayer.userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                userScore = calcUserScore(userPoints, newPlayer.salary, newPlayer.userScoreMultiplier);
            }

            if (!player.captain_pointsChangedManually && isGameActive) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId(), Const.CAPTAIN_MODE);
                captain_userPoints = NumberHelper.float(
                    calcSympathyPoints(newPlayer.captain_userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                captain_userScore = calcUserScore(
                    captain_userPoints,
                    newPlayer.captain_salary,
                    newPlayer.userScoreMultiplier,
                );
            }

            const {projOwn = {}, ...newPlayerProps} = newPlayer;

            if ('projOwn' in player) {
                player.projOwn.setValueFromPlayerPayload(newPlayer);
            }

            result[key] = {
                ...player,
                ...newPlayerProps,
                pointsChangedManually: player.pointsChangedManually,
                userPoints,
                userScore,
                exposureMax: player.exposureMax,
                exposureMin: player.exposureMin,
                captain_pointsChangedManually: player.captain_pointsChangedManually,
                captain_userPoints,
                captain_userScore,
                captain_exposureMax: player.captain_exposureMax,
                captain_exposureMin: player.captain_exposureMin,
                overall_exposureMax: player.overall_exposureMax,
            };
        });

        return {...result};
    }

    applySympathy({incomingValues: {players, playersSympathy}}) {
        return this.exec('applySympathy_ShowDown', {players, playersSympathy});
    }

    preparePlayerForLineup({incomingValues = {}}) {
        return this.exec('preparePlayerForLineup_ShowDown', incomingValues);
    }

    prepareRandomPoints({incomingValues: {}}) {
        return this.exec('prepareRandomPointsByModes', {});
    }

    updatePlayersWhoseGamesBecameInactive({incomingValues: {dispatch, players}}) {
        return this.exec('updatePlayersWhoseGamesBecameInactive_ShowDown', {
            dispatch,
            players,
            modes: this.modes,
        });
    }

    updatePlayersWhoseGamesBecameActive({incomingValues: {dispatch, players}}) {
        return this.exec('updatePlayersWhoseGamesBecameActive_ShowDown', {
            dispatch,
            players,
            modes: this.modes,
        });
    }
}
